import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import MainLayout from './components/layout/MainLayout';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './App.css';

// 懒加载页面组件
const CompetitorShops = React.lazy(() => import('./pages/competitor/Shops'));
const CompetitorShopGroups = React.lazy(() => import('./pages/competitor/ShopGroups'));
const CompetitorScrape = React.lazy(() => import('./pages/competitor/Scrape'));
const CompetitorAnalysis = React.lazy(() => import('./pages/competitor/Analysis'));
const SelfShops = React.lazy(() => import('./pages/self/Shops'));
const SelfShopGroups = React.lazy(() => import('./pages/self/ShopGroups'));
const SelfScrape = React.lazy(() => import('./pages/self/Scrape'));
const SelfAnalysis = React.lazy(() => import('./pages/self/Analysis'));
const XhsShops = React.lazy(() => import('./pages/xhs/Shops'));
const XhsShopGroups = React.lazy(() => import('./pages/xhs/ShopGroups'));
const XhsScrape = React.lazy(() => import('./pages/xhs/Scrape'));
const XhsAnalysis = React.lazy(() => import('./pages/xhs/Analysis'));
const XhsDataVisualization = React.lazy(() => import('./pages/xhs/DataVisualization'));
const Login = React.lazy(() => import('./pages/auth/Login'));
const Register = React.lazy(() => import('./pages/auth/Register'));
const InviteCodes = React.lazy(() => import('./pages/admin/InviteCodes'));

// 加载中组件
const Loading = () => <div style={{ padding: '20px', textAlign: 'center' }}>页面加载中...</div>;

// 受保护的路由
const ProtectedRoute: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
};

// 管理员路由
const AdminRoute: React.FC = () => {
  const { isAdmin, isLoading } = useAuth();

  if (isLoading) {
    return <Loading />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};



function App() {
  return (
    <Router>
      <AuthProvider>
        <React.Suspense fallback={<Loading />}>
          <Routes>
            {/* 登录和注册页面 - 独立路由，不受其他逻辑影响 */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* 受保护路由 - 需要登录 */}
            <Route element={<ProtectedRoute />}>
              {/* 默认重定向到小红书店铺管理页 */}
              <Route path="/" element={<Navigate to="/xhs/shops" replace />} />
              
              {/* 使用MainLayout作为布局 */}
              <Route element={<MainLayout />}>
                {/* 对标店铺路由 */}
                <Route path="/competitor/shop-groups" element={<CompetitorShopGroups />} />
                <Route path="/competitor/shops" element={<CompetitorShops />} />
                <Route path="/competitor/scrape" element={<CompetitorScrape />} />
                <Route path="/competitor/analysis" element={<CompetitorAnalysis />} />
                
                {/* 自己店铺路由 */}
                <Route path="/self/shop-groups" element={<SelfShopGroups />} />
                <Route path="/self/shops" element={<SelfShops />} />
                <Route path="/self/scrape" element={<SelfScrape />} />
                <Route path="/self/analysis" element={<SelfAnalysis />} />
                
                {/* 小红书路由 */}
                <Route path="/xhs/shop-groups" element={<XhsShopGroups />} />
                <Route path="/xhs/shops" element={<XhsShops />} />
                <Route path="/xhs/scrape" element={<XhsScrape />} />
                <Route path="/xhs/analysis" element={<XhsAnalysis />} />
                <Route path="/xhs/display" element={<XhsDataVisualization />} />
                
                {/* 管理员路由 */}
                <Route element={<AdminRoute />}>
                  <Route path="/admin/invite-codes" element={<InviteCodes />} />
                </Route>
              </Route>
            </Route>
            
            {/* 404路由 */}
            <Route path="*" element={<div>404 - 页面不存在</div>} />
          </Routes>
        </React.Suspense>
      </AuthProvider>
    </Router>
  );
}

export default App;
